import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AlertStatus } from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import { Button, Input, Modal, Textarea } from '@/components/ui';

/**
 * Handlingerne paa en alarm. PRD par. 8.1 og par. 8.4.
 *
 * Ligger her og ikke i siderne, fordi baade listen og detaljesiden skal kunne udfoere dem, og
 * fordi de to steder skal opfoere sig ens: samme dialog, samme krav om en begrundelse, samme
 * fejlbesked. To kopier ville drive fra hinanden - typisk saadan at det ene sted glemmer at
 * kraeve begrundelsen, som serveren saa afviser.
 *
 * Hvilke handlinger der er lovlige, afgoeres af serveren. Knapperne her skjuler dem der ikke
 * giver mening i den aktuelle tilstand, men det er en hjaelp og ikke selve reglen.
 */

export type AlertAction = 'start' | 'snooze' | 'resolve' | 'dismiss';

const ACTION_LABELS: Record<AlertAction, string> = {
  start: 'Tag ansvar',
  snooze: 'Udsæt',
  resolve: 'Markér som løst',
  dismiss: 'Afvis',
};

/**
 * Overgangene fra PRD par. 8.4.
 *
 * Der er ingen vej ud af `resolved` og `dismissed`: en alarm der gaelder alligevel, kommer
 * tilbage ved at detekteringsmotoren rejser den paa ny. Derfor har arkivet ingen knapper.
 */
const AVAILABLE: Record<AlertStatus, readonly AlertAction[]> = {
  open: ['start', 'snooze', 'resolve', 'dismiss'],
  in_progress: ['snooze', 'resolve', 'dismiss'],
  snoozed: ['start', 'resolve', 'dismiss'],
  resolved: [],
  dismissed: [],
};

export function availableAlertActions(status: AlertStatus): readonly AlertAction[] {
  return AVAILABLE[status];
}

interface Dialog {
  id: string;
  action: Exclude<AlertAction, 'start'>;
}

/**
 * Deler mutation, dialogtilstand og fejlbesked mellem de to sider.
 *
 * `start` gaar direkte igennem - der er ingenting at spoerge om. De tre andre aabner dialogen,
 * fordi de kraever enten en begrundelse eller en dato.
 */
export function useAlertActions(onDone?: () => void) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [note, setNote] = useState('');
  const [snoozeUntil, setSnoozeUntil] = useState('');

  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['alerts'] }),
      queryClient.invalidateQueries({ queryKey: ['alert'] }),
      // Alarmtallene staar ogsaa paa dashboardet og paa kontrakterne. Bliver de staaende
      // uaendrede efter en handling, ser appen ud til at have mistet den.
      queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      queryClient.invalidateQueries({ queryKey: ['contracts'] }),
    ]);
  };

  const act = useMutation({
    mutationFn: (input: { id: string; action: string; body?: unknown }) =>
      api.post(`/alerts/${input.id}/${input.action}`, input.body),
    onSuccess: async () => {
      setError(null);
      await invalidate();
      onDone?.();
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : 'Handlingen fejlede.'),
  });

  const close = (): void => {
    setDialog(null);
    setNote('');
    setSnoozeUntil('');
  };

  const run = (id: string, action: AlertAction): void => {
    setError(null);
    if (action === 'start') {
      act.mutate({ id, action: 'start' });
      return;
    }
    setDialog({ id, action });
  };

  const submit = (): void => {
    if (!dialog) return;

    const body =
      dialog.action === 'snooze'
        ? { snoozed_until: new Date(snoozeUntil).toISOString(), resolution_note: note }
        : { resolution_note: note };

    act.mutate({ id: dialog.id, action: dialog.action, body }, { onSuccess: close });
  };

  return {
    error,
    busy: act.isPending,
    run,
    dialog,
    dialogProps: {
      dialog,
      note,
      setNote,
      snoozeUntil,
      setSnoozeUntil,
      close,
      submit,
      busy: act.isPending,
    },
  };
}

export type AlertDialogProps = ReturnType<typeof useAlertActions>['dialogProps'];

const DIALOG_TITLES: Record<Dialog['action'], string> = {
  snooze: 'Udsæt alarm',
  resolve: 'Markér alarm som løst',
  dismiss: 'Afvis alarm',
};

const DIALOG_HELP: Record<Dialog['action'], string> = {
  snooze: 'Alarmen kommer automatisk tilbage på den valgte dato.',
  resolve: 'Begrundelsen gemmes i revisionssporet og kan læses i arkivet.',
  /*
   * Afvisning er en vurdering og ikke en oprydning, og teksten siger det: motoren rejser ikke
   * alarmen igen, saa laenge tallene bag den er de samme. Uden den saetning ville "Afvis" og
   * "Markér som løst" ligne to ord for det samme.
   */
  dismiss:
    'Alarmen rejses ikke igen, så længe grundlaget er uændret. Begrundelsen gemmes i ' +
    'revisionssporet.',
};

export function AlertActionDialog(props: AlertDialogProps) {
  const { dialog, note, setNote, snoozeUntil, setSnoozeUntil, close, submit, busy } = props;

  return (
    <Modal
      open={dialog !== null}
      onClose={close}
      title={dialog ? DIALOG_TITLES[dialog.action] : ''}
      description={dialog ? DIALOG_HELP[dialog.action] : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            Annullér
          </Button>
          <Button
            loading={busy}
            disabled={dialog?.action === 'snooze' ? !snoozeUntil : note.trim().length < 3}
            onClick={submit}
          >
            Bekræft
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {dialog?.action === 'snooze' ? (
          <div>
            <span className="field-label">Kom tilbage den</span>
            <Input
              type="datetime-local"
              value={snoozeUntil}
              onChange={(event) => setSnoozeUntil(event.target.value)}
            />
          </div>
        ) : null}
        <div>
          <span className="field-label">
            Begrundelse{dialog?.action === 'snooze' ? ' (valgfri)' : ''}
          </span>
          <Textarea value={note} onChange={(event) => setNote(event.target.value)} />
        </div>
      </div>
    </Modal>
  );
}

/** Knapperaekken. Samme raekkefoelge og samme ordlyd de to steder den bruges. */
export function AlertActionButtons({
  status,
  busy,
  onAct,
  size = 'sm',
}: {
  status: AlertStatus;
  busy: boolean;
  onAct: (action: AlertAction) => void;
  size?: 'sm' | 'md';
}) {
  return (
    <>
      {availableAlertActions(status).map((action) => (
        <Button
          key={action}
          size={size}
          variant={action === 'dismiss' ? 'ghost' : 'secondary'}
          disabled={busy}
          onClick={() => onAct(action)}
        >
          {ACTION_LABELS[action]}
        </Button>
      ))}
    </>
  );
}
